import { useCallback, useRef, type PointerEvent as ReactPointerEvent, type ReactNode, type KeyboardEvent } from 'react'

type ResizableThProps = {
  columnId: string
  label?: string
  width?: number
  minWidth?: number
  className?: string
  children?: ReactNode
  onResize: (columnId: string, width: number) => void
}

const RESIZE_STEP = 8

export const ResizableTh = ({
  columnId,
  label,
  width,
  minWidth = 56,
  className,
  children,
  onResize,
}: ResizableThProps) => {
  const thRef = useRef<HTMLTableCellElement>(null)
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null)

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLSpanElement>) => {
      event.preventDefault()
      event.stopPropagation()
      const th = thRef.current
      if (!th) {
        return
      }
      const startWidth = width ?? th.getBoundingClientRect().width
      dragRef.current = { startX: event.clientX, startWidth }
      event.currentTarget.setPointerCapture(event.pointerId)

      const handleMove = (moveEvent: PointerEvent) => {
        const drag = dragRef.current
        if (!drag) {
          return
        }
        const next = Math.max(minWidth, drag.startWidth + (moveEvent.clientX - drag.startX))
        onResize(columnId, next)
      }

      const handleUp = (upEvent: PointerEvent) => {
        dragRef.current = null
        window.removeEventListener('pointermove', handleMove)
        window.removeEventListener('pointerup', handleUp)
        try {
          event.currentTarget.releasePointerCapture(upEvent.pointerId)
        } catch {
          // already released
        }
      }

      window.addEventListener('pointermove', handleMove)
      window.addEventListener('pointerup', handleUp)
    },
    [columnId, minWidth, onResize, width],
  )

  // Клавиатурное управление — стрелки ±8px
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLSpanElement>) => {
      const currentWidth = width ?? (thRef.current?.getBoundingClientRect().width ?? minWidth)

      switch (event.key) {
        case 'ArrowRight': {
          event.preventDefault()
          onResize(columnId, Math.min(currentWidth + RESIZE_STEP, 1200))
          break
        }

        case 'ArrowLeft': {
          event.preventDefault()
          onResize(columnId, Math.max(currentWidth - RESIZE_STEP, minWidth))
          break
        }

        default:
          break
      }
    },
    [columnId, minWidth, width, onResize],
  )

  const content = children !== undefined ? children : label

  return (
    <th
      ref={thRef}
      className={['market-table__th--resizable', className].filter(Boolean).join(' ')}
      style={width != null ? { width, minWidth: Math.min(minWidth, width) } : { minWidth }}
      scope="col"
      aria-label={!content && label ? label : undefined}
    >
      {content}
      <span
        className="market-table__resize-handle"
        role="slider"
        aria-orientation="vertical"
        aria-label={label ? `Изменить ширину столбца «${label}»` : 'Изменить ширину столбца'}
        aria-valuenow={Math.round(width ?? minWidth)}
        aria-valuemin={minWidth}
        aria-valuemax={1200}
        tabIndex={0}
        onPointerDown={handlePointerDown}
        onKeyDown={handleKeyDown}
      />
    </th>
  )
}
